import { CircleAlert, MessageSquare } from 'lucide-react';

/**
 * The three inset diagrams for <Problems>. Each depicts the shape of a problem
 * rather than reporting a measurement — no figure here is a claim about Boundr's
 * results, and the section carries a visible "illustrative" note to that effect.
 *
 * All three are built from tokens and hairlines so they inherit light/dark.
 */

const ENCROACHMENT_STEPS = ['Marker moved', 'Fence creeps', 'Structure goes up'];

/** A plot outline with one edge quietly eaten into. */
export function EncroachmentVisual() {
  return (
    <div className="flex flex-col gap-4">
      <div className="relative h-24 border-2 border-dashed border-foreground/30">
        {/* The strip stands in for land taken; the solid edge is the line as it
            stands now, against the dashed line where it should be. */}
        <div className="absolute inset-y-0 right-0 w-[30%] border-l-2 border-ring bg-ring/15" />
        <span className="absolute bottom-1 right-2 text-xs font-medium text-ring">taken</span>
        <span className="absolute bottom-1 left-2 text-xs text-foreground/50">your plot</span>
      </div>

      <ol className="flex flex-col gap-2">
        {ENCROACHMENT_STEPS.map((step, idx) => (
          <li key={step} className="flex items-center gap-2 text-sm text-foreground/70">
            <span className="flex size-5 shrink-0 items-center justify-center rounded-full border border-border text-xs text-foreground/60">
              {idx + 1}
            </span>
            {step}
          </li>
        ))}
      </ol>
    </div>
  );
}

const UPDATES = [
  { date: '12 Jan', text: 'Everything is fine', ok: true },
  { date: '—', text: 'No update for four months', ok: false },
  { date: '9 May', text: 'Everything is fine', ok: true },
];

/** The update log a remote owner actually gets: thin, undated, unverifiable. */
export function UpdatesVisual() {
  return (
    <div className="flex flex-col gap-4">
      <ul className="flex flex-col gap-2">
        {UPDATES.map(({ date, text, ok }) => (
          <li key={date} className="flex items-start gap-2 rounded-lg bg-secondary/60 px-3 py-2">
            {ok ? (
              <MessageSquare className="mt-0.5 size-4 shrink-0 text-foreground/40" />
            ) : (
              <CircleAlert className="mt-0.5 size-4 shrink-0 text-ring" />
            )}
            <div className="min-w-0">
              <p className="text-xs text-foreground/50">{date}</p>
              <p className={`text-sm ${ok ? 'text-foreground/70' : 'text-ring'}`}>{text}</p>
            </div>
          </li>
        ))}
      </ul>
      <p className="text-xs leading-snug text-foreground/55">
        No photos, no dates you can check, nothing you can act on.
      </p>
    </div>
  );
}

// Widths are relative weight, not measured data — see the file header.
const DISTANCE_COSTS = [
  { label: 'Flights & Transportation', weight: 90 },
  { label: 'Days off work', weight: 70 },
  { label: 'Time zones & Travel', weight: 45 },
  { label: 'Family time', weight: 100 },
];

/** What one confirmation trip costs an owner living abroad. */
export function DistanceVisual() {
  return (
    <div className="flex flex-col gap-4">
      <ul className="flex flex-col gap-3">
        {DISTANCE_COSTS.map(({ label, weight }) => (
          <li key={label} className="flex flex-col gap-1.5">
            <span className="text-sm text-foreground/70">{label}</span>
            <span className="block h-2 w-full rounded-full bg-secondary">
              <span className="block h-2 rounded-full bg-ring/70" style={{ width: `${weight}%` }} />
            </span>
          </li>
        ))}
      </ul>
      <p className="text-xs leading-snug text-foreground/55">
        Spent to confirm that nothing has changed.
      </p>
    </div>
  );
}
